import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { RecordExpenseSaveOptions } from '@/components/spending/record-expense-save-options';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { ExpenseAccountField } from '@/components/spending/expense-account-field';
import { ExpenseCategorySelect } from '@/components/shared/expense-category-select';
import { ExpenseExternalPartySelect } from '@/components/shared/expense-external-party-select';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import {
  loadRecordExpensePreferences,
  persistRecordExpensePreferences,
} from '@/lib/record-expense-preferences';
import { ApiError } from '@/lib/api-errors';
import { useChurchStore } from '@/stores/church-store';
import { formatMoney } from '@/utils/format';

type PayeeKind = 'internal' | 'external' | 'other';

const PAYEE_KINDS: PayeeKind[] = ['internal', 'external', 'other'];

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

export interface RecordExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RecordExpenseDialog = ({
  open,
  onOpenChange,
}: RecordExpenseDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountCurrency, setAccountCurrency] = useState<string | null>(null);
  const resolvedCurrency = accountCurrency || church?.baseCurrency || 'GHS';

  const [payeeKind, setPayeeKind] = useState<PayeeKind>('internal');
  const [payee, setPayee] = useState('');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [externalPartyId, setExternalPartyId] = useState<string | null>(null);
  const [externalPartyName, setExternalPartyName] = useState('');
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [expenseDate, setExpenseDate] = useState<string | null>(null);
  const [memo, setMemo] = useState('');
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordExpensePreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);

  const resetEntryFields = () => {
    setPayee('');
    setMemberId(null);
    setMemberQuery('');
    setExternalPartyId(null);
    setExternalPartyName('');
    setAmountMinor(null);
    setMemo('');
  };

  const resetForm = () => {
    setPayeeKind('internal');
    resetEntryFields();
    setSubmitConfirmOpen(false);
    setCategoryId(null);
    setAccountId(null);
    setAccountCurrency(null);
    setExpenseDate(null);
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'fund-expense-payee', memberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${
          memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''
        }`,
      ),
    enabled: open && payeeKind === 'internal',
    placeholderData: keepPreviousData,
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );
  const selectedMember = memberOptions.find((option) => option.id === memberId) ?? null;

  const payeeReady =
    payeeKind === 'internal'
      ? Boolean(memberId)
      : payeeKind === 'external'
        ? Boolean(externalPartyId)
        : Boolean(payee.trim());

  const submitPayeeName =
    payeeKind === 'internal'
      ? (selectedMember?.label ?? '')
      : payeeKind === 'external'
        ? externalPartyName
        : payee.trim();

  const canSubmit =
    payeeReady &&
    Boolean(categoryId) &&
    Boolean(accountId) &&
    amountMinor != null &&
    Boolean(expenseDate);

  const create = useMutation({
    mutationFn: () =>
      api.post('/expenses', {
        payeeKind,
        payee: payeeKind === 'other' ? payee.trim() : undefined,
        memberId: payeeKind === 'internal' ? memberId : undefined,
        externalPartyId: payeeKind === 'external' ? externalPartyId : undefined,
        categoryId,
        financialPositionItemId: accountId,
        amountMinor,
        currency: resolvedCurrency,
        expenseDate,
        description: memo.trim() ? memo.trim() : null,
        submit: true,
      }),
    onSuccess: () => {
      toast.success(t('expenses.saved'));
      setSubmitConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      if (savePreferences.addAnother) {
        resetEntryFields();
        setFormKey((current) => current + 1);
        return;
      }
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <>
      <ConfirmDialog
        open={submitConfirmOpen}
        onOpenChange={setSubmitConfirmOpen}
        title={t('confirm.submitExpense.title', {
          name: submitPayeeName,
          amount:
            amountMinor != null
              ? formatMoney(amountMinor, resolvedCurrency)
              : formatMoney(0, resolvedCurrency),
        })}
        description={t('confirm.submitExpense.description')}
        confirmLabel={t('confirm.submitExpense.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={create.isPending}
        onConfirm={() => {
          if (!canSubmit) return;
          create.mutate();
        }}
      />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            key={formKey}
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) return;
              setSubmitConfirmOpen(true);
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{t('expenses.create')}</DialogTitle>
                <DialogDescription>{t('expenses.accountPlaceholder')}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.payeeType')}</Label>
                <Listbox
                  value={payeeKind}
                  onChange={(next) => {
                    const kind = (next as PayeeKind) ?? 'internal';
                    setPayeeKind(kind);
                    setPayee('');
                    setMemberId(null);
                    setMemberQuery('');
                    setExternalPartyId(null);
                    setExternalPartyName('');
                  }}
                  options={PAYEE_KINDS.map((kind) => ({
                    value: kind,
                    label: t(`expenses.payeeKinds.${kind}`),
                  }))}
                  popoverPortal="dialog"
                />
              </div>
              {payeeKind === 'internal' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('expenses.member')}</Label>
                  <EntityCombobox
                    value={memberId}
                    onChange={setMemberId}
                    options={memberOptions}
                    selected={selectedMember}
                    onQueryChange={setMemberQuery}
                    loading={members.isPending && !members.data}
                    popoverPortal="dialog"
                    clearLabel={tCommon('action.clearSelection')}
                    placeholder={t('expenses.memberPlaceholder')}
                  />
                </div>
              ) : null}
              {payeeKind === 'external' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('expenses.externalParty')}</Label>
                  <ExpenseExternalPartySelect
                    value={externalPartyId}
                    onChange={(id, party) => {
                      setExternalPartyId(id);
                      setExternalPartyName(party?.name ?? '');
                    }}
                    popoverPortal="dialog"
                  />
                </div>
              ) : null}
              {payeeKind === 'other' ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="fund-expense-payee-other">{t('expenses.payee')}</Label>
                  <Input
                    id="fund-expense-payee-other"
                    value={payee}
                    onChange={(event) => setPayee(event.target.value)}
                    placeholder={t('expenses.payeeOtherPlaceholder')}
                    required
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.category')}</Label>
                <ExpenseCategorySelect
                  value={categoryId}
                  onChange={setCategoryId}
                  popoverPortal="dialog"
                />
              </div>
              <ExpenseAccountField
                open={open}
                value={accountId}
                onChange={(id, nextCurrency) => {
                  setAccountId(id);
                  setAccountCurrency(nextCurrency);
                }}
              />
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.amount')}</Label>
                <MoneyInput
                  value={amountMinor}
                  onChange={setAmountMinor}
                  currency={resolvedCurrency}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('expenses.date')}</Label>
                <DatePicker value={expenseDate} onChange={setExpenseDate} popoverPortal="dialog" />
                <Tip tipId="spending.periodLock" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fund-expense-memo">{t('expenses.memo')}</Label>
                <Textarea
                  id="fund-expense-memo"
                  value={memo}
                  onChange={(event) => setMemo(event.target.value)}
                  placeholder={t('expenses.memoPlaceholder')}
                  rows={2}
                />
              </div>
              <RecordExpenseSaveOptions
                addAnother={savePreferences.addAnother}
                onAddAnotherChange={(addAnother) => {
                  const next = { ...savePreferences, addAnother };
                  setSavePreferences(next);
                  persistRecordExpensePreferences(church?.id, next);
                }}
                disabled={create.isPending}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button type="submit" loading={create.isPending} disabled={!canSubmit}>
                  {t('expenses.submit')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
